const imported_stylus_components = require('.cache/react-style-loader/src/components/AppRouter/Nav.styl');
import _ from 'lodash';
import { Button, Icon, Menu, MenuDivider, MenuItem, Switch } from '@blueprintjs/core';
import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useWindowResize } from '@pi/ui';
import { useSessionQuery } from 'src/generated/graphql';
import PinnedTaskDefinition from 'src/pages/TaskDefinition/components/PinnedTaskDefinition';
import { useLogout } from 'src/hooks/useLogout';

import { useAppContext } from '../AppContext';
import routes from './routes';

import type { User } from 'src/generated/graphql';
import type { RouteDefinition } from './routes';

const Nav: React.FC = () => {
    const { darkMode, navCollapsed, update } = useAppContext();
    const [isResponsiveSmall, setIsResponsiveSmall] = useState(false);
    const session = useSessionQuery();
    const logout = useLogout();

    useWindowResize({
        throttle: 10,
        direction: 'x',
        callback: () => setIsResponsiveSmall(window.innerWidth < 70 * 16),
    });

    const small = isResponsiveSmall || navCollapsed;

    const user = session.data?.session?.user as User;

    if (!user) return null;

    return (
        <Root large className={navCollapsed ? 'nav-collapsed' : ''}>
            <RouteRenderer small={small} user={user} />
            <div style={{ flex: '1 1 0' }} />
            <PinnedTaskDefinition />
            {!isResponsiveSmall && (
                <>
                    <div className='hide-on-small'>
                        <MenuItem
                            icon={navCollapsed ? 'menu-open' : 'menu-closed'}
                            text='Collapse'
                            onClick={() => update({ navCollapsed: !navCollapsed })}
                        />
                    </div>
                    <Button
                        minimal
                        className='show-on-small'
                        icon={navCollapsed ? 'menu-open' : 'menu-closed'}
                        onClick={() => update({ navCollapsed: !navCollapsed })}
                    />
                </>
            )}
            <div className='bp4-menu-item hide-on-small'>
                <Icon
                    icon='lightbulb'
                    className='relative'
                    style={{ color: 'inherit', top: '3px', marginLeft: '1px' }}
                />
                <Switch
                    checked={darkMode}
                    onChange={() => update({ darkMode: !darkMode })}
                    alignIndicator='right'
                    label={'Lights ' + (darkMode ? 'off' : 'on')}
                    large
                    style={{ flex: '1 1 0' }}
                />
            </div>
            <Button
                icon={<Icon icon='lightbulb' style={{ color: 'inherit' }} />}
                minimal
                className='show-on-small'
                onClick={() => update({ darkMode: !darkMode })}
            />
            <div className='bp4-menu-item hide-on-small'>
                <Button
                    intent='danger'
                    icon='log-out'
                    onPointerDown={logout}
                    large
                    style={{ flex: '1 1 0' }}
                    data-testid='logout'
                >
                    Log Out
                </Button>
            </div>
            <Button
                intent='danger'
                icon='log-out'
                minimal
                className='show-on-small mb-5'
                onPointerDown={logout}
            />
        </Root>
    );
};

export default Nav;

export const RouteRenderer: React.FC<{ small: boolean; user: User }> = ({ small, user }) => {
    const location = useLocation();

    const render = (route: RouteDefinition, path: string[]) => {
        if (!route.isActive(user)) return null;

        const childRoutes = Object.entries(route.children || {})
            .map(([key, v]) => ({ ...v, key }))
            .filter(x => !x.hideFromNav);
        const groups = _.groupBy(childRoutes || [], 'group');

        const renderGroup = (
            group: string | null | undefined,
            children: typeof childRoutes,
        ): React.ReactNode => {
            if (!children.length) return null;
            const result = children.map(child => render(child, [...path, child.key]));
            if (path.length) {
                result.unshift(<MenuDivider key='div' className='show-on-small' />);
                result.unshift(
                    <MenuDivider key='title' title={route.name} className='show-on-small' />,
                );
            }
            if (!group || group === 'undefined' || group === 'null') return result;
            return (
                <React.Fragment key={group}>
                    <li
                        className='bp4-menu-header pt05 hide-on-small'
                        style={{ fontSize: '1.125rem' }}
                    >
                        {group}
                    </li>
                    {result}
                </React.Fragment>
            );
        };

        let children: React.ReactNode = null;

        if (_.size(groups) > 1) {
            children = Object.values(groups).map(list => renderGroup(list[0].group, list));
        } else if (_.size(groups) === 1) {
            children = renderGroup(null, childRoutes);
        }

        if (!route.component || route.hideFromNav) return children;

        const url = '/' + [...path, ...(route.params || [])].join('/');

        const regexpComponents = [...path, ...(route.params?.map(() => '[^/]*') ?? [])];

        const active = regexpComponents.every((_, i) =>
            new RegExp('^\\/' + regexpComponents.slice(0, i + 1).join('\\/') + '(\\/|$)').test(
                location.pathname,
            ),
        );

        return (
            <Link to={url} style={{ color: 'inherit' }}>
                <MenuItem
                    icon={route.icon}
                    text={small && path.length <= 1 ? null : route.name}
                    active={active}
                    data-hide-arrow={path.length === 1 && small}
                >
                    {children}
                </MenuItem>
            </Link>
        );
    };

    return <>{render(routes, [])}</>;
};

const Root = imported_stylus_components.Root.withComponent(Menu);